import { Injectable, inject } from '@angular/core';
import { CanActivate, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../environments/environment';

/**
 * Guard that checks if user has admin access (dashboard.write permission)
 * Redirects to home page if user lacks permission
 * Pre-fetches admin token with Metrics.Write scope before loading admin routes
 */
@Injectable({
  providedIn: 'root'
})
export class AdminGuard implements CanActivate {
  private readonly authService = inject(AuthService);
  private readonly msalService = inject(MsalService);
  private readonly router = inject(Router);

  async canActivate(): Promise<boolean> {
    // In no-auth mode, allow access
    if (environment.noAuthMode) {
      return true;
    }

    // Check if user has admin access via role
    if (!this.authService.hasAdminAccess()) {
      console.warn('Access denied: User lacks dashboard.write permission');
      this.router.navigate(['/']);
      return false;
    }

    // User has admin access - pre-fetch token with Write scope
    // This warms the token cache and reduces latency on first admin API call
    try {
      const account = this.authService.getAccount();
      if (account) {
        await this.msalService.instance.acquireTokenSilent({
          scopes: environment.msal.scopes.write,
          account: account
        });
        console.debug('AdminGuard: Pre-fetched admin token with Metrics.Write scope');
      }
    } catch (error) {
      // Log but don't block - AdminScopeInterceptor will handle token acquisition
      console.warn('AdminGuard: Failed to pre-fetch admin token', error);
    }

    return true;
  }
}
